import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { CheckButtonList, Container, FormFooter, Navigation, NavBar } from '../../components';
import { REQUESTING, SUCCESS } from '../../../actions/statusTypes';
import { __WEB__, COLOR, FONT, THEME } from '../../../constants/styles';
import { equalsSet } from '../../../reducers/helpers';
import {
  getCMRAppointmentServices,
  resetCMRAttachedServicesState,
  updateCMRAppointmentServices,
} from './slice';

const SAVE_SUCCESS_MESSAGE = 'CMR appointment services updated.';

function getDisplayedServices(catalogServices, attachedServiceIds) {
  const attachedIds = new Set(attachedServiceIds);
  return catalogServices.filter(
    (service) => service.isActive || attachedIds.has(service.serviceId)
  );
}

function getEffectiveSelectedServiceIds(
  displayedServices,
  initialAttachedServiceIds,
  selectedServices
) {
  const displayedServiceIds = new Set(displayedServices.map((service) => service.serviceId));
  const hiddenAttachedServiceIds = initialAttachedServiceIds.filter(
    (serviceId) => !displayedServiceIds.has(serviceId)
  );
  const selectedDisplayedServiceIds = selectedServices
    .filter((service) => service.checked)
    .map((service) => service.id);

  return selectedDisplayedServiceIds.concat(hiddenAttachedServiceIds);
}

const CMRAttachedServicesForm = memo((props) => {
  useEffect(
    NavBar.setup(
      props,
      !__WEB__ && {
        buttons: { rightButtons: [NavBar.buttons.done] },
      }
    ),
    []
  );

  const dispatch = useDispatch();
  const catalogEmployerId = useSelector((state) => state.screens.cmrRevenueCycle.employerId);
  const catalogStatus = useSelector((state) => state.screens.cmrRevenueCycle.servicesStatus);
  const catalogData = useSelector((state) => state.screens.cmrRevenueCycle.servicesData || []);
  const catalogError = useSelector((state) => state.screens.cmrRevenueCycle.servicesError);
  const appointmentServicesStatus = useSelector(
    (state) => state.screens.scheduler.cmrAppointmentServicesStatus
  );
  const appointmentServicesData = useSelector(
    (state) => state.screens.scheduler.cmrAppointmentServicesData || []
  );
  const appointmentServicesError = useSelector(
    (state) => state.screens.scheduler.cmrAppointmentServicesError
  );
  const updateStatus = useSelector(
    (state) => state.screens.scheduler.updateCMRAppointmentServicesStatus
  );
  const updateError = useSelector(
    (state) => state.screens.scheduler.updateCMRAppointmentServicesError
  );

  const [selectedServices, setSelectedServices] = useState([]);
  const [submittedServiceIds, setSubmittedServiceIds] = useState(null);
  const [hasInitializedServices, setHasInitializedServices] = useState(false);
  const [savedServiceIds, setSavedServiceIds] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const hasLoaded =
    catalogEmployerId === props.employerId &&
    catalogStatus === SUCCESS &&
    appointmentServicesStatus === SUCCESS;
  const isLoading = catalogStatus === REQUESTING || appointmentServicesStatus === REQUESTING;
  const isSaving = updateStatus === REQUESTING;
  const formattedAppointmentDate = useMemo(() => {
    if (!props.appointmentDate || !moment(props.appointmentDate).isValid()) return null;
    return moment(props.appointmentDate).format('MMM DD, YYYY').toUpperCase();
  }, [props.appointmentDate]);

  const initialAttachedServiceIds = useMemo(() => {
    return appointmentServicesData.map((service) => service.serviceId);
  }, [appointmentServicesData]);
  const savedAttachedServiceIds = savedServiceIds ?? initialAttachedServiceIds;

  const displayedServices = useMemo(() => {
    return getDisplayedServices(catalogData, savedAttachedServiceIds);
  }, [catalogData, savedAttachedServiceIds]);

  useEffect(() => {
    setSelectedServices([]);
    setSubmittedServiceIds(null);
    setHasInitializedServices(false);
    setSavedServiceIds(null);
    setShowSuccessMessage(false);
    dispatch(resetCMRAttachedServicesState());
    if (props.employerId && props.eventId) {
      dispatch(
        getCMRAppointmentServices({
          employerId: props.employerId,
          eventId: props.eventId,
        })
      );
    }

    return () => {
      dispatch(resetCMRAttachedServicesState());
    };
  }, [dispatch, props.employerId, props.eventId]);

  useEffect(() => {
    if (!hasLoaded) return;

    const attachedServiceIds = new Set(savedAttachedServiceIds);
    setSelectedServices((currentServices) => {
      const currentServicesById = new Map(currentServices.map((service) => [service.id, service]));
      return displayedServices.map((service) => ({
        id: service.serviceId,
        label: service.isActive ? (
          service.name
        ) : (
          <Text style={styles.inactiveServiceLabel}>{`${service.name} (Inactive)`}</Text>
        ),
        checked:
          currentServicesById.get(service.serviceId)?.checked ??
          attachedServiceIds.has(service.serviceId),
        editable: true,
      }));
    });
    setHasInitializedServices(true);
  }, [displayedServices, hasLoaded, savedAttachedServiceIds]);

  const effectiveSelectedServiceIds = useMemo(() => {
    return getEffectiveSelectedServiceIds(
      displayedServices,
      savedAttachedServiceIds,
      selectedServices
    );
  }, [displayedServices, savedAttachedServiceIds, selectedServices]);

  useEffect(() => {
    if (submittedServiceIds && updateStatus === SUCCESS) {
      setSavedServiceIds(submittedServiceIds);
      setShowSuccessMessage(true);
      setSubmittedServiceIds(null);
    }
  }, [submittedServiceIds, updateStatus]);

  const hasChanges = useMemo(() => {
    return hasLoaded && !equalsSet(effectiveSelectedServiceIds, new Set(savedAttachedServiceIds));
  }, [effectiveSelectedServiceIds, hasLoaded, savedAttachedServiceIds]);

  const handleServicesChanged = useCallback((services) => {
    setShowSuccessMessage(false);
    setSelectedServices(services);
  }, []);

  const handleCancel = useCallback(() => {
    Navigation.close(props);
  }, [props]);

  const handleUpdate = useCallback(() => {
    if (!hasLoaded || isSaving || !hasChanges) return;

    setShowSuccessMessage(false);
    setSubmittedServiceIds(effectiveSelectedServiceIds);
    dispatch(
      updateCMRAppointmentServices({
        employerId: props.employerId,
        eventId: props.eventId,
        serviceIds: effectiveSelectedServiceIds,
      })
    );
  }, [
    dispatch,
    effectiveSelectedServiceIds,
    hasChanges,
    hasLoaded,
    isSaving,
    props.employerId,
    props.eventId,
  ]);

  const footer = (
    <FormFooter
      onCancel={handleCancel}
      onSave={handleUpdate}
      cancelLabel="Cancel"
      saveLabel="Update"
      saveDisabled={!hasLoaded || isSaving || !hasChanges}
      renderSpinner={isSaving}
      forceHorizontal={true}
      horizontalStyle={styles.footer}
      cancelBtnStyle={styles.footerButton}
      saveBtnStyle={styles.footerButton}
    />
  );

  return (
    <Container
      scrollView={true}
      stickyFooter={footer}
      contentContainerStyle={styles.contentContainer}
      renderActivityOverlay={isLoading || isSaving}
      successMessage={showSuccessMessage ? SAVE_SUCCESS_MESSAGE : null}
      errorMessage={
        updateError?.message || appointmentServicesError?.message || catalogError?.message || null
      }
    >
      <View style={styles.appointmentHeader}>
        <View style={[styles.appointmentHeaderColumn, styles.patientHeaderColumn]}>
          <Text style={styles.patientName}>{props.patientName || 'Patient'}</Text>
        </View>
        <View style={styles.appointmentHeaderColumn}>
          <Text style={styles.appointmentDate}>
            {formattedAppointmentDate ? `CMR DAY: ${formattedAppointmentDate}` : 'CMR DAY'}
          </Text>
        </View>
      </View>
      <View style={styles.servicesContainer}>
        <Text style={styles.sectionTitle}>ATTACHED SERVICES</Text>
        {hasInitializedServices && selectedServices.length > 0 && (
          <CheckButtonList
            items={selectedServices}
            multi={true}
            scrollable={false}
            // buttonSize="large"
            noBottomBorder={true}
            containerStyle={styles.serviceList}
            rowContainerStyle={styles.serviceRow}
            labelStyle={styles.serviceLabel}
            onValuesChanged={handleServicesChanged}
          />
        )}
        {hasInitializedServices && selectedServices.length === 0 && (
          <View style={styles.emptyStateContainer}>
            <Text style={THEME.textRegular}>No CMR services are available.</Text>
          </View>
        )}
      </View>
    </Container>
  );
});

const styles = StyleSheet.create({
  contentContainer: {
    flexGrow: 1,
  },
  appointmentHeader: {
    flexDirection: 'row',
    minHeight: 90,
    backgroundColor: COLOR.altContrast,
    borderBottomWidth: 1,
    borderBottomColor: COLOR.altAccent,
  },
  appointmentHeaderColumn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 25,
    paddingVertical: 20,
  },
  patientHeaderColumn: {
    borderRightWidth: 1,
    borderRightColor: COLOR.altAccent,
  },
  patientName: {
    ...FONT.semibold,
    color: COLOR.text,
    fontSize: 16,
    textAlign: 'center',
  },
  appointmentDate: {
    ...FONT.semibold,
    color: COLOR.text,
    fontSize: 16,
    letterSpacing: 1,
    textAlign: 'center',
  },
  servicesContainer: {
    flex: 1,
    minHeight: __WEB__ ? 540 : 360,
    paddingHorizontal: __WEB__ ? 65 : 20,
    paddingTop: __WEB__ ? 65 : 35,
    paddingBottom: 40,
    backgroundColor: COLOR.contrast,
  },
  sectionTitle: {
    ...FONT.semibold,
    color: COLOR.text,
    fontSize: 16,
    letterSpacing: 1.25,
    marginBottom: 15,
  },
  serviceList: {
    backgroundColor: COLOR.contrast,
  },
  serviceRow: {
    paddingHorizontal: 0,
    paddingVertical: 8,
  },
  serviceLabel: {
    ...FONT.medium,
    color: COLOR.darkest,
    fontSize: 16,
    marginLeft: 25,
  },
  inactiveServiceLabel: {
    color: COLOR.textLight,
  },
  emptyStateContainer: {
    paddingVertical: 20,
  },
  footer: {
    paddingHorizontal: __WEB__ ? 40 : 30,
    paddingVertical: 30,
  },
  footerButton: {
    minWidth: __WEB__ ? 160 : 118,
  },
});

export default CMRAttachedServicesForm;
